import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useDateRange } from "@/context/DateRangeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { inr, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Undo2, TrendingUp } from "lucide-react";
import ChannelBreakdown from "@/components/ChannelBreakdown";
import ReturnsUploadDialog from "@/components/ReturnsUploadDialog";
import AdSpendUploadDialog from "@/components/AdSpendUploadDialog";

const EMPTY = {
  date: todayISO(),
  total_orders: 0, prepaid_orders: 0, cod_orders: 0,
  gross_revenue: 0, discounts: 0,
  meta_ad_spend: 0, google_ad_spend: 0,
  shipping_fee: 0, payment_gateway_fee_percent: 2.36,
  total_units_sold: 0, total_cogs: 0,
};

export default function SalesLog() {
  const { current } = useWorkspace();
  const { dateFrom, dateTo, setDateFrom, setDateTo } = useDateRange();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [returnsOpen, setReturnsOpen] = useState(false);
  const [adSpendOpen, setAdSpendOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    if (!current) return;
    setLoading(true);
    try {
      const { data } = await api.get("/sales-logs", { params: { workspace_id: current.id, date_from: dateFrom, date_to: dateTo } });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current, refreshKey, dateFrom, dateTo]);

  const startAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };

  const numify = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [
    k, ["date"].includes(k) ? v : Number(v),
  ]));

  const save = async (e) => {
    e.preventDefault();
    const payload = numify({
      date: form.date,
      total_orders: form.total_orders, prepaid_orders: form.prepaid_orders, cod_orders: form.cod_orders,
      gross_revenue: form.gross_revenue, discounts: form.discounts,
      meta_ad_spend: form.meta_ad_spend, google_ad_spend: form.google_ad_spend,
      shipping_fee: form.shipping_fee, payment_gateway_fee_percent: form.payment_gateway_fee_percent,
      total_units_sold: form.total_units_sold, total_cogs: form.total_cogs,
    });
    try {
      if (editing) {
        await api.put(`/sales-logs/${editing.id}`, payload);
        toast.success("Log updated");
      } else {
        await api.post("/sales-logs", payload, { params: { workspace_id: current.id } });
        toast.success("Log saved");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete log for ${s.date}?`)) return;
    await api.delete(`/sales-logs/${s.id}`);
    toast.success("Deleted");
    load();
  };

  const netRevenue = useMemo(() => Number(form.gross_revenue || 0) - Number(form.discounts || 0), [form.gross_revenue, form.discounts]);
  const pgCost = useMemo(() => (netRevenue * Number(form.payment_gateway_fee_percent || 0)) / 100, [netRevenue, form.payment_gateway_fee_percent]);
  const totalAd = Number(form.meta_ad_spend || 0) + Number(form.google_ad_spend || 0);

  if (!current) return <div className="p-12 text-muted-foreground">No workspace selected.</div>;

  const field = (key, label, opts = {}) => (
    <div>
      <Label className="overline">{label}</Label>
      <Input
        type={opts.type || "number"}
        step={opts.step || "0.01"}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="rounded-none mt-2 font-mono"
        data-testid={`log-${key}`}
      />
    </div>
  );

  return (
    <div className="min-h-screen">
      <div className="border-b border-border p-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-muted-foreground">{current.brand_name}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Daily Sales & Ad Log</h1>
          <p className="text-sm text-muted-foreground mt-2">{items.length} days logged</p>
        </div>
        <div className="flex items-end gap-2">
        <div>
          <Label className="overline">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-none mt-1 h-10 w-40 font-mono" data-testid="saleslog-date-from" />
        </div>
        <div>
          <Label className="overline">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-none mt-1 h-10 w-40 font-mono" data-testid="saleslog-date-to" />
        </div>
        <Button variant="outline" onClick={() => setAdSpendOpen(true)} className="rounded-none" data-testid="upload-ad-spend-btn">
          <TrendingUp className="h-4 w-4 mr-1" /> Upload Ad Spend
        </Button>
        <Button variant="outline" onClick={() => setReturnsOpen(true)} className="rounded-none" data-testid="upload-returns-btn">
          <Undo2 className="h-4 w-4 mr-1" /> Upload Returns / RTO File
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startAdd} className="rounded-none" data-testid="add-log-btn"><Plus className="h-4 w-4 mr-1" /> Add Entry</Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Log" : "New Daily Log"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="overline">Date</Label>
                  <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none mt-2 font-mono" data-testid="log-date" />
                </div>
                {field("total_orders", "Total Orders", { step: "1" })}
                {field("prepaid_orders", "Prepaid", { step: "1" })}
                {field("cod_orders", "COD", { step: "1" })}
                {field("gross_revenue", "Gross Revenue ₹")}
                {field("discounts", "Discounts ₹")}
                {field("meta_ad_spend", "Meta Ad ₹")}
                {field("google_ad_spend", "Google Ad ₹")}
                {field("shipping_fee", "Shipping ₹")}
                {field("payment_gateway_fee_percent", "PG Fee %")}
                {field("total_units_sold", "Units Sold", { step: "1" })}
                {field("total_cogs", "Total COGS ₹")}
              </div>
              <div className="grid grid-cols-3 gap-4 border border-dashed border-border p-3">
                <div><div className="overline text-muted-foreground">Net Revenue</div><div className="font-mono text-lg mt-1" data-testid="log-net-revenue">{inr(netRevenue, { decimals: 2 })}</div></div>
                <div><div className="overline text-muted-foreground">PG Cost</div><div className="font-mono text-lg mt-1" data-testid="log-pg-cost">{inr(pgCost, { decimals: 2 })}</div></div>
                <div><div className="overline text-muted-foreground">Total Ad Spend</div><div className="font-mono text-lg mt-1" data-testid="log-total-ad">{inr(totalAd, { decimals: 2 })}</div></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
                <Button type="submit" className="rounded-none" data-testid="log-save">{editing ? "Save" : "Create"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="p-8">
        <div className="mb-6">
          <ChannelBreakdown workspaceId={current.id} dateFrom={items[items.length - 1]?.date || new Date().toISOString().slice(0, 10)} dateTo={items[0]?.date || new Date().toISOString().slice(0, 10)} />
        </div>
        <div className="border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="overline">Date</TableHead>
                <TableHead className="overline text-right">Orders</TableHead>
                <TableHead className="overline text-right">Gross</TableHead>
                <TableHead className="overline text-right">Discount</TableHead>
                <TableHead className="overline text-right">Net Rev</TableHead>
                <TableHead className="overline text-right">Ads</TableHead>
                <TableHead className="overline text-right">Ship</TableHead>
                <TableHead className="overline text-right">PG</TableHead>
                <TableHead className="overline text-right">COGS</TableHead>
                <TableHead className="overline text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="log-table">
              {loading && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
              {!loading && items.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No entries.</TableCell></TableRow>}
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`log-row-${s.date}`}>
                  <TableCell className="font-mono text-xs">{s.date}</TableCell>
                  <TableCell className="text-right font-mono">{s.total_orders}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.gross_revenue)}</TableCell>
                  <TableCell className="text-right font-mono text-[hsl(var(--negative))]">{inr(s.discounts)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{inr(s.net_revenue)}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.total_ad_spend)}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.shipping_fee)}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.pg_cost)}</TableCell>
                  <TableCell className="text-right font-mono">{inr(s.total_cogs)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(s)} data-testid={`edit-log-${s.date}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`del-log-${s.date}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <ReturnsUploadDialog open={returnsOpen} onOpenChange={setReturnsOpen} onCommitted={() => setRefreshKey((k) => k + 1)} />
      <AdSpendUploadDialog open={adSpendOpen} onOpenChange={setAdSpendOpen} onCommitted={() => setRefreshKey((k) => k + 1)} />
    </div>
  );
}
